import React, { useMemo, useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { History, Search, Users, Eye, Phone, Filter, RotateCcw } from 'lucide-react';
import { api } from '../../utils/api';

const DriverWorkHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('approved'); // Default: 'approved' ('approved', 'pending', 'blocked')
  const [regionalOfficeFilter, setRegionalOfficeFilter] = useState('');
  const [developerOnly, setDeveloperOnly] = useState('Exclude');
  const [currentPage, setCurrentPage] = useState(1);
  const [regionalOffices, setRegionalOffices] = useState([]);
  const PAGE_SIZE = 10;

  // Fetch regional offices for dropdown
  useEffect(() => {
    const fetchRegionalOffices = async () => {
      try {
        const response = await api.get('/regional-offices', { params: { limit: 100 } });
        const offices = response?.data?.data?.offices || response?.data?.offices || response?.data || [];
        setRegionalOffices(Array.isArray(offices) ? offices : []);
      } catch (error) {
        console.error('Error fetching regional offices:', error);
      }
    };
    fetchRegionalOffices();
  }, []);

  const { data, isLoading, isFetching, error } = useQuery(
    ['driver-work-history-drivers', searchTerm, status, regionalOfficeFilter, developerOnly],
    async () => {
      const params = {
        page: 1,
        limit: 1000,
        developerOnly
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (regionalOfficeFilter) {
        params.regionalOfficeFilter = regionalOfficeFilter;
      }

      if (status === 'approved') {
        params.isApproved = 'true';
        params.isBlocked = 'false';
      } else if (status === 'pending') {
        params.isApproved = 'false';
        params.isBlocked = 'false';
      } else if (status === 'blocked') {
        params.isBlocked = 'true';
      }

      const response = await api.get('/drivers', { params });
      return response?.data?.data?.drivers || response?.data?.drivers || [];
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false
    }
  );

  const filteredDrivers = useMemo(() => {
    const items = Array.isArray(data) ? data : [];
    return items.filter((driver) => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const name = String(driver?.name || '').toLowerCase();
        const phone = String(driver?.phone || '').toLowerCase();
        const id = String(driver?._id || driver?.id || '').toLowerCase();
        if (!name.includes(term) && !phone.includes(term) && !id.includes(term)) {
          return false;
        }
      }
      if (status === 'approved') {
        if (!driver?.isApproved || driver?.isBlocked) return false;
      } else if (status === 'blocked') {
        if (!driver?.isBlocked) return false;
      } else if (status === 'pending') {
        if (driver?.isApproved || driver?.isBlocked) return false;
      }
      return true;
    });
  }, [data, searchTerm, status]);

  const totalPages = Math.ceil(filteredDrivers.length / PAGE_SIZE);

  const drivers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredDrivers.slice(start, start + PAGE_SIZE);
  }, [filteredDrivers, currentPage]);

  const clearFilters = () => {
    setSearchTerm('');
    setStatus('approved');
    setRegionalOfficeFilter('');
    setDeveloperOnly('Exclude');
    setCurrentPage(1);
  };

  const getFilterClasses = (filterValue, isDev = false) => {
    const isActive = isDev
      ? filterValue !== 'Exclude'
      : (filterValue && filterValue !== '' && filterValue !== 'approved');
    return isActive
      ? "border-2 border-indigo-500 bg-indigo-50/30 text-indigo-900 font-medium rounded-lg px-3 py-2 text-sm w-full transition-all duration-200 shadow-sm outline-none"
      : "border border-gray-300 bg-white text-gray-700 rounded-lg px-3 py-2 text-sm w-full transition-all duration-200 hover:border-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none";
  };

  useEffect(() => {
    if (error) {
      toast.error(error?.response?.data?.message || 'Failed to fetch drivers list');
    }
  }, [error]);

  const hasActiveFilters = searchTerm || status !== 'approved' || regionalOfficeFilter || developerOnly !== 'Exclude';

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">Driver Work History</h1>
              <p className="page-subtitle">Filter drivers and open detailed work history</p>
            </div>
            <Link to="/drivers" className="btn btn-outline">
              <Users className="h-4 w-4 mr-2" />
              Back To Drivers
            </Link>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <h3 className="card-title">Filters</h3>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="btn btn-outline btn-sm text-xs flex items-center gap-1"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Clear Filters
            </button>
          )}
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* Driver Name / Search */}
            <div>
              <label className="form-label text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1 block">
                Driver Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  placeholder="Name, phone, or ID"
                  className={`${getFilterClasses(searchTerm)} pl-10`}
                />
              </div>
            </div>

            {/* Status Filter (Approved, Pending, Blocked) */}
            <div>
              <label className="form-label text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1 block">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => { setStatus(e.target.value); setCurrentPage(1); }}
                className={getFilterClasses(status)}
              >
                <option value="">All Statuses</option>
                <option value="approved">Approved</option>
                <option value="pending">Pending</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>

            {/* Regional Office Filter */}
            <div>
              <label className="form-label text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1 block">
                Regional Office
              </label>
              <select
                value={regionalOfficeFilter}
                onChange={(e) => { setRegionalOfficeFilter(e.target.value); setCurrentPage(1); }}
                className={getFilterClasses(regionalOfficeFilter)}
              >
                <option value="">All Offices</option>
                {regionalOffices.map((office) => (
                  <option key={office._id} value={office._id}>
                    {office.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Developer Filter */}
            <div>
              <label className="form-label text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1 block">
                Developer Filter
              </label>
              <select
                value={developerOnly}
                onChange={(e) => { setDeveloperOnly(e.target.value); setCurrentPage(1); }}
                className={getFilterClasses(developerOnly, true)}
              >
                <option value="Exclude">Exclude Developers</option>
                <option value="Include">Include Developers</option>
                <option value="Only">Developers Only</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <h3 className="card-title">
              Drivers ({filteredDrivers.length})
            </h3>
            {isFetching && (
              <span className="text-xs text-indigo-600 animate-pulse font-medium">
                Updating...
              </span>
            )}
          </div>
        </div>
        <div className="card-body">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="loading-spinner"></div>
            </div>
          ) : drivers.length === 0 ? (
            <div className="empty-state">
              <History className="empty-state-icon" />
              <h3 className="empty-state-title">No drivers found</h3>
              <p className="empty-state-description">Try adjusting your filters or search criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Driver</th>
                    <th className="table-header-cell">Phone</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Action</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {drivers.map((driver) => (
                    <tr key={String(driver?._id || driver?.id)} className="table-row">
                      <td className="table-cell">
                        <div className="font-medium text-gray-900">{driver?.name || 'N/A'}</div>
                        <div className="text-xs text-gray-500">ID: {String(driver?._id || 'N/A')}</div>
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center text-sm text-gray-700">
                          <Phone className="h-3.5 w-3.5 mr-2 text-gray-400" />
                          {driver?.phone || 'N/A'}
                        </div>
                      </td>
                      <td className="table-cell">
                        {driver?.isBlocked ? (
                          <span className="badge badge-danger">Blocked</span>
                        ) : driver?.isApproved ? (
                          <span className="badge badge-success">Approved</span>
                        ) : (
                          <span className="badge badge-warning">Pending</span>
                        )}
                      </td>
                      <td className="table-cell">
                        <Link to={`/drivers/driverworkhistory/${driver?._id}`} className="btn btn-outline btn-sm">
                          <Eye className="h-4 w-4 mr-2" />
                          View Work History
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredDrivers.length)} of {filteredDrivers.length} drivers
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                  className="btn btn-outline btn-sm"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="btn btn-outline btn-sm"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverWorkHistory;
